import { Container, Text } from 'pixi.js';
import type { Disposable } from '../../core/Event';
import type { PlinkoModel } from '../../model/plinko/PlinkoModel';
import { BalanceDisplay } from '../../widgets/BalanceDisplay';
import { PrimaryButton } from '../../widgets/PrimaryButton';
import type { SceneComponent, SceneLayoutContext } from '../SceneComponent';

const FONT_FAMILY = 'system-ui, -apple-system, sans-serif';

export interface PlinkoHudCallbacks {
  onDrop: () => void;
}

/**
 * Plinko HUD: balance, DROP button, status label. Subscribes to model
 * events for balance + result text.
 */
export class PlinkoHudComponent extends Container implements SceneComponent {
  private readonly balance: BalanceDisplay;
  private readonly dropButton: PrimaryButton;
  private readonly statusLabel: Text;
  private readonly subscriptions: Disposable[] = [];

  constructor(
    private readonly model: PlinkoModel,
    private readonly callbacks: PlinkoHudCallbacks,
  ) {
    super();
    this.balance = new BalanceDisplay(this.model.balance);
    this.dropButton = new PrimaryButton({
      label: this.idleLabel(),
      width: 200,
      fillActive: 0xff6bd6,
      fillHover: 0x6b3d77,
      textColor: 0xffffff,
      onTap: () => this.callbacks.onDrop(),
    });
    this.statusLabel = new Text({
      text: 'tap DROP · land in a slot for a multiplier',
      style: {
        fontFamily: FONT_FAMILY,
        fontSize: 13,
        fontWeight: '500',
        fill: 0xffffff,
        align: 'center',
      },
    });
    this.statusLabel.anchor.set(0.5);
    this.statusLabel.alpha = 0.7;

    this.addChild(this.balance, this.dropButton, this.statusLabel);

    this.subscriptions.push(
      this.model.events.on('balance-changed', () => {
        this.balance.setAmount(this.model.balance);
      }),
      this.model.events.on('not-enough-coins', (event) => {
        this.statusLabel.text = `not enough coins for ${event.required}`;
      }),
      this.model.events.on('ball-landed', (event) => {
        if (event.winnings > 0) {
          this.statusLabel.text = `${event.multiplier}× → +${event.winnings.toLocaleString()}`;
        } else {
          this.statusLabel.text = `${event.multiplier}× → no payout`;
        }
      }),
    );
  }

  layout(ctx: SceneLayoutContext): void {
    const { centerX, machineTop, spinButtonY } = ctx;
    this.balance.x = centerX;
    this.balance.y = Math.max(16, machineTop - 96);
    this.dropButton.x = centerX;
    this.dropButton.y = spinButtonY;
    this.statusLabel.x = centerX;
    this.statusLabel.y = spinButtonY + 56;
  }

  destroy(): void {
    for (const sub of this.subscriptions) sub.dispose();
    this.subscriptions.length = 0;
    this.balance.destroyDisplay();
    super.destroy({ children: true });
  }

  setDropping(dropping: boolean): void {
    this.dropButton.setLabel(dropping ? '…' : this.idleLabel());
    this.dropButton.setSuppressHover(dropping);
  }

  private idleLabel(): string {
    return `DROP · ${this.model.bet}`;
  }
}
